import {
	type COUNTRYTYPE,
	CURRENCYCONFIG,
	type CURRENCYCONFIGTYPE,
	FXCONVERSION,
	type FXCONVERSION_TYPE,
	FXRATE,
	type FXRATE_TYPE,
	getFxRateByCountry,
	getSafeObjectField,
	type IUseCurrentFXRate,
} from 'BreetHelpers';
import { useCurrencyOutput } from 'BreetHooks';
import { selectAuthSettings, selectFiatMeta, useAppSelector } from 'BreetRedux';

export const useCurrentFxRate = ({
	fxRate = FXRATE.DEFAULT,
	conversionType = FXCONVERSION.SELLUSD,
}: {
	fxRate?: FXRATE_TYPE;
	conversionType?: FXCONVERSION_TYPE;
}): IUseCurrentFXRate => {
	const currencyOutput = useCurrencyOutput();

	const fiatMeta = useAppSelector(selectFiatMeta);

	const authSettings = useAppSelector(selectAuthSettings);

	const mapCountryLower: Partial<Record<FXCONVERSION_TYPE, Lowercase<COUNTRYTYPE>>> = {
		[FXCONVERSION.USDToNGN]: 'nigeria',
		[FXCONVERSION.USDToGHS]: 'ghana',
		[FXCONVERSION.FIATToUSD]: fiatMeta.countryLower,
	};

	const countryLower = mapCountryLower[conversionType];

	const currentRate = getFxRateByCountry(
		countryLower as unknown as Lowercase<COUNTRYTYPE>,
		fxRate === FXRATE.BUY ? authSettings?.fxRates.buy : authSettings?.fxRates
	);

	const rateObj: Record<FXCONVERSION_TYPE, number> = {
		[FXCONVERSION.NGNToGHS]: authSettings?.fxRates.NGNToGHS ?? 0,
		[FXCONVERSION.GHSToNGN]: authSettings?.fxRates.NGNToGHS ? 1 / authSettings.fxRates.NGNToGHS : 0,
		[FXCONVERSION.USDToNGN]: currentRate,
		[FXCONVERSION.USDToGHS]: currentRate,
		[FXCONVERSION.FIATToUSD]: currentRate,
		[FXCONVERSION.SELLUSD]: currentRate,
	};

	const rate = currencyOutput(rateObj[conversionType]);

	const fromCurrencyObjectMapping: Record<FXCONVERSION_TYPE, CURRENCYCONFIGTYPE> = {
		[FXCONVERSION.NGNToGHS]: CURRENCYCONFIG.NGN,
		[FXCONVERSION.GHSToNGN]: CURRENCYCONFIG.GHS,
		[FXCONVERSION.USDToNGN]: CURRENCYCONFIG.USD,
		[FXCONVERSION.USDToGHS]: CURRENCYCONFIG.USD,
		[FXCONVERSION.FIATToUSD]: getSafeObjectField(CURRENCYCONFIG, fiatMeta.currency, CURRENCYCONFIG.USD),
		[FXCONVERSION.SELLUSD]: CURRENCYCONFIG.USD,
	};

	const fromCurrencyObject = fromCurrencyObjectMapping[conversionType];

	const toCurrencyObjectMapping: Record<FXCONVERSION_TYPE, CURRENCYCONFIGTYPE> = {
		[FXCONVERSION.NGNToGHS]: CURRENCYCONFIG.GHS,
		[FXCONVERSION.GHSToNGN]: CURRENCYCONFIG.NGN,
		[FXCONVERSION.USDToNGN]: CURRENCYCONFIG.NGN,
		[FXCONVERSION.USDToGHS]: CURRENCYCONFIG.GHS,
		[FXCONVERSION.FIATToUSD]: CURRENCYCONFIG.USD,
		[FXCONVERSION.SELLUSD]: getSafeObjectField(CURRENCYCONFIG, fiatMeta.currency, CURRENCYCONFIG.USD),
	};

	const toCurrencyObject = toCurrencyObjectMapping[conversionType];

	const rateInfoFrom = fromCurrencyObject.symbol !== '$' ? fromCurrencyObject.symbol : toCurrencyObject.symbol;
	const rateInfoTo = fromCurrencyObject.symbol !== '$' ? toCurrencyObject.symbol : fromCurrencyObject.symbol;
	const rateInfo = `${rateInfoFrom}${rate} / ${rateInfoTo}`;

	return {
		rate,
		rateInfo,
		rateInfoSymbol: {
			from: rateInfoFrom,
			to: rateInfoTo,
		},
		symbol: {
			from: fromCurrencyObject.symbol,
			to: toCurrencyObject.symbol,
		},
		currencyProps: {
			from: fromCurrencyObject,
			to: toCurrencyObject,
		},
	};
};
